import { computed, defineComponent, PropType } from "vue";
import { CalendarOptions, EventContentArg } from "@fullcalendar/core";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";
import { mapActions, mapState } from "pinia";
import { useApp } from "../../../../App/useApp";

import { IGetTaskResponseByUser } from "../../../../../services/user-service";
import { NavigationService } from "../../../../../services/navigation.service";
import { ITaskByPatientResponse, TaskService } from "../../../../../services/task.service";
import { ToastrService } from "../../../../../services/toastr.service";
import { IGetAgency } from "../../../../../services/agency.service";
import { PatientCalendarModel } from "../../PatientCalendar.model";
import { RescheduleTask } from "../../../../../models/task.model";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import {
    ApplicationEnum,
    ApplicationNameEnum,
    ParentPermissionEnum,
    PermissionActionEnum,
    VisitStatusEnum,
    EvvStatusHomeCareEnum,
    EvvStatusHomeHealthEnum
} from "../../../../../enums/enums";
import {
    IVisitDetails,
    IRescheduleVisitHosPal,
    IReassignVisitHC,
    IHandleTaskReschuleIssue,
    IReassignCalendarVisitHC,
    IGetTaskComment
} from "../../../../../resources-vue/vue-interfaces/i-visit-details";
import { IBulkDeletePayload } from "../../../../../resources-vue/vue-interfaces/i-bulk-reassign-visit";
import { IReassignVisitDetails } from "../../../../../resources-vue/vue-interfaces/i-reassign-visit";
import { IDeleteTaskPayload, IRestoreMissedVisitRequest } from "../../../../../resources-vue/vue-interfaces/i-task";
import { adjustDate } from "../../../../../common/adjust-date";
import { FormatPhoneNumber } from "../../../../../resources/value-converters/format-phone";
import { GetProductClass } from "../../../../../resources/value-converters/get-product-class";
import { formatName } from "../../../../../common/format-name";
import { BulkReassign } from "../../../../../resources-vue/vue-models/bulk-reassign";
import config from "../../../../../common/config";
import { IPromptOptions } from "../../../../../resources-vue/vue-dialogs/prompt/prompt";
import { useLoadingIndicator } from "../../../../Common/LoadingIndicator/store/useLoadingIndicator";
import { IRescheduleValidations } from "../../../../../resources-vue/vue-dialogs/RescheduleValidations/RescheduleValidation";
import {
    formatScheduledTime,
    formatTimeInOut,
    getTaskReturnComments
} from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { getHospiceRedirectionUrl } from "../../../../../common/get-hospice-redirection-url";
import { CommentColorEnum } from "../../../../../enums/CommentColorEnum";
import { StickyNoteTypeEnum } from "../../../../../enums/StickyNoteTypeEnum";
import { helperTestingAgency } from "../../../../../common/vue-helpers/testing-agency-check";
import { AnalyticsService, AnalyticsCategory } from "../../../../../services/analytics.service";
import { isMobileScreenSize } from "../../../../../utilities/isMobileScreenSize";
import { truncateText } from "../../../../../utilities/truncateString";

import VisitDetails from "../../../../../resources-vue/vue-dialogs/VisitDetails/VisitDetails.vue";
import RescheduleValidation from "../../../../../resources-vue/vue-dialogs/RescheduleValidations/RescheduleValidation.vue";
import ScheduleVisitDialog from "../../../../../resources-vue/vue-dialogs/ScheduleVisit/ScheduleVisit.vue";
import FullCalendar from "../../../../../resources-vue/vue-elements/FullCalendar/FullCalendar.vue";
import PostToAxxesscare from "../../../../../resources-vue/vue-dialogs/PostToAxxesscare/PostToAxxesscare.vue";
import ReassignVisit from "../../../../../resources-vue/vue-dialogs/ReassignVisit/ReassignVisit.vue";
import PatientProfile from "../../../../../resources-vue/vue-dialogs/PatientProfile/PatientProfile.vue";
import Prompt from "../../../../../resources-vue/vue-dialogs/prompt/prompt.vue";
import EvvDetails from "../../../../../resources-vue/vue-dialogs/EvvDetails/EvvDetails.vue";

enum ScheduleLegendState {
    info = 1,
    success = 2,
    danger = 3
}

export default defineComponent({
    props: {
        startDate: { type: Date },
        endDate: { type: Date },
        patientsList: { type: Array as PropType<PatientCalendarModel[]>, default: [] },
        tasksList: { type: Array, default: [] },
        onCalendarRefresh: { type: Function }
    },

    components: {
        FullCalendar
    },

    data() {
        return {
            _analyticsService: null as AnalyticsService,
            _navigationService: null,
            _permissionManager: null,
            _taskService: null as TaskService,
            _toastrService: null,
            _dialogService: null,
            dialogRef: null,
            isReassignAllowed: false as boolean,
            isEditVisitDetailsAllowed: false as boolean,
            showEditBtn: false as boolean,
            tasksMap: null,
            // NOTE: eventLimit be one more than the required events, as to account for dummy event
            eventLimit: 4 as number,
            blueNote: [] as any,
            showPostToAxxessCareTest: false,
            organizationId: null as string,
            agencyList: [] as IGetAgency[],
            userNamesMap: null,
            getTaskReturnComments,
            isMobileDevice: false as boolean,
            canViewTaskNotesHos: false as boolean,
            canViewTaskNotesPal: false as boolean
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    emits: ["calendarRefresh"],

    created() {
        this._navigationService = Container.instance.get(NavigationService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._analyticsService = Container.instance.get(AnalyticsService);
    },
    async mounted() {
        this.isMobileDevice = isMobileScreenSize();
        this.canViewTaskNotesHos = this._permissionManager.checkPermissionByProduct(
            ParentPermissionEnum.taskNotes,
            PermissionActionEnum.canView,
            ApplicationEnum.AxxessHospiceFE
        );
        this.canViewTaskNotesPal = this._permissionManager.checkPermissionByProduct(
            ParentPermissionEnum.taskNotes,
            PermissionActionEnum.canView,
            ApplicationEnum.AxxessPalliative
        );
        const hasTestAgency = await helperTestingAgency.checkHasTestingAgency();
        this.showPostToAxxessCareTest = hasTestAgency;
        this.isReassignAllowed = this._permissionManager.checkPermission(
            ParentPermissionEnum.clinician,
            PermissionActionEnum.canReassign
        );
        this.isEditVisitDetailsAllowed = this._permissionManager.checkPermission(
            ParentPermissionEnum.scheduler,
            PermissionActionEnum.canEdit
        );
        this.showEditBtn = this.isReassignAllowed || this.isEditVisitDetailsAllowed;
        this.organizationId = window.localStorage.getItem(config.organizationId);
        this.agencyList = this.organizations.find(
            (organization: { id: string }) => organization.id === this.organizationId
        ).agencies;
    },

    computed: {
        ...mapState(useApp, ["organizations"]),
        patientDetails() {
            this.userNamesMap = new Map();
            const taskNoteContent = this.tasksList as IVisitDetails[];
            taskNoteContent.forEach((comment: IVisitDetails) => {
                if (comment.Comments !== null) {
                    let CommentsList = comment.Comments;
                    CommentsList.forEach((note: IGetTaskComment) => {
                        const alreadyExist = this.blueNote.some(
                            (blnote) => blnote.id == comment.PatientId && blnote.message == note?.Comment
                        );
                        if (note?.CommentColor === "Blue" && !alreadyExist) {
                            this.blueNote.push({
                                id: comment.PatientId,
                                element: `<span id=${`blue-note-${comment.PatientId}`}><i id=${`blue-tooltip-${comment.PatientId}`} class="me-2 fas fa-sticky-note blue-note cursor-pointer"></i></span>`,
                                message: note?.Comment
                            });
                        }
                    });
                }
            });

            return (
                this.patientsList?.map((patient) => {
                    this.userNamesMap.set(
                        patient.Id,
                        formatName(patient.FirstName, patient.LastName, "LN, FN", "", patient.MiddleInitial)
                    );
                    return {
                        ...patient,
                        id: patient.Id,
                        groupId: patient.Id
                    };
                }) ?? []
            );
        },

        visitActions() {
            this.tasksMap = new Map();
            this.tasksList.forEach((task: any) => {
                let startDate = new Date(Date.parse(task.StartDate));
                let key = `${task.PatientId}${startDate.toLocaleDateString()}`;
                if (!this.tasksMap.has(key)) {
                    this.tasksMap.set(key, [task]);
                } else {
                    let currentTasks = this.tasksMap.get(key);
                    this.tasksMap.set(key, [...currentTasks, task]);
                }
            });
            let tasksList: ITaskByPatientResponse[] = [];
            this.tasksMap.forEach((value: any) => {
                tasksList.push(...value);
                // not removing it as would still look for any better solution

                // let list = value.slice(0, this.eventLimit);
                // tasksList = tasksList.concat([...list]);
                // if (value.length > this.eventLimit) {
                //     let extraTask = {
                //         Id: value[0].Id,
                //         PatientId: value[0].PatientId,
                //         StartDate: moment(value[0].StartDate).endOf("day").format(),
                //         EndDate: value[0].StartDate,
                //         isExtraEvent: true
                //     } as ITaskByPatientResponse;
                //     tasksList.push(extraTask);
                // }
            });
            return (
                tasksList?.map((task) => ({
                    ...task,
                    id: task.Id,
                    start: task.StartDate,
                    end: moment(task.EndDate).endOf("day").format(),
                    resourceId: task.PatientId
                })) ?? []
            );
        },
        calendarOptions(): CalendarOptions {
            return {
                resources: this.patientDetails,
                resourceOrder: "LastName, FirstName MiddleInitial",
                events: this.visitActions,
                resourceGroupField: "groupId",
                resourceAreaHeaderContent: "Patient Name",
                resourceAreaWidth: this.isMobileDevice ? 210 : 310,
                droppable: true,
                contentHeight: "auto",
                eventDragStart: (event: any) => {
                    $('[role="tooltip"]')?.remove();
                },
                drop: (event: any) => {
                    $('[role="tooltip"]')?.remove();
                    const patientId = event.draggedEl.id.split("@@")[1] || null;
                    if (!patientId || !(event.resource.id == patientId)) {
                        this.filterDuplicateEvent(event);
                        throw new Error();
                    }
                    const eventId = event.draggedEl.id.split("@@")[0];
                    const currentTask =
                        (this.tasksList?.find((task: ITaskByPatientResponse) => task.Id == eventId) as any) || null;
                    if (!currentTask) {
                        this._toastrService.error({
                            title: `Error - Rescheduling task`,
                            message: `There was an error rescheduling the visit. Please Try again later.`
                        });
                        this.$emit("calendarRefresh");
                        return;
                    }
                    this.rescheduleVisit(currentTask, moment(event.date).format("MM/DD/YYYY"));
                    this.filterDuplicateEvent(event);
                },
                eventDrop: async (event: any) => {
                    $('[role="tooltip"]')?.remove();
                    const rowChanged = !(
                        JSON.stringify(event.oldEvent._def.resourceIds) == JSON.stringify(event.event._def.resourceIds)
                    );
                    if (rowChanged) {
                        event.revert();
                        return;
                    }
                    const visitDetails = event.event.extendedProps;
                    if (![VisitStatusEnum.NotStarted, VisitStatusEnum.NotYetDue].includes(visitDetails.Status)) {
                        this._toastrService.error({
                            title: `Error`,
                            message: `Visit must be in Not yet Started and Not Yet Due status.`
                        });
                        event.revert();
                        return;
                    }

                    if (
                        ![EvvStatusHomeCareEnum.NotStarted].includes(
                            visitDetails.EvvIconStatus as EvvStatusHomeCareEnum
                        ) ||
                        ![EvvStatusHomeHealthEnum.NotStarted].includes(
                            visitDetails.VerificationVisitStatus as EvvStatusHomeHealthEnum
                        )
                    ) {
                        this._toastrService.error({
                            title: `Error`,
                            message: `Visit EVV status must be in Not Started status.`
                        });
                        event.revert();
                        return;
                    }

                    if (!event?.event?.extendedProps || !event?.event?.startStr) {
                        this._toastrService.error({
                            title: `Error - Rescheduling task`,
                            message: `There was an error rescheduling the visit. Please Try again later.`
                        });
                        this.$emit("calendarRefresh");
                        return;
                    }
                    const lastDay = moment(visitDetails.StartDate).endOf("week").format("YYYYMMDD");

                    if (
                        visitDetails.StartDate != visitDetails.EndDate &&
                        moment(event.event.start).format("YYYYMMDD") == lastDay
                    ) {
                        this._toastrService.error({
                            title: `Error - Rescheduling task`,
                            message: `Midnight split visit will not get reschedule to the last day of the week`
                        });
                        this.$emit("calendarRefresh");
                        return;
                    }

                    if (
                        [ApplicationEnum.AxxessHospiceFE, ApplicationEnum.AxxessPalliative].includes(
                            visitDetails.Application
                        )
                    ) {
                        let startTime = visitDetails.StartTime
                            ? moment(visitDetails.StartTime).format("HH:mm:ss")
                            : "00:00:00";
                        let endTime = visitDetails.EndTime
                            ? moment(visitDetails.EndTime).format("HH:mm:ss")
                            : "00:00:00";
                        this.rescheduleVisitHosPal(
                            visitDetails,
                            `${moment(event.event.start).format("MM/DD/YYYY")}  ${startTime}`,
                            `${moment(event.event.end).format("MM/DD/YYYY")} ${endTime}`
                        );
                    } else if (visitDetails.Application == ApplicationEnum.HomeCare) {
                        let timeIn = visitDetails.StartTime || visitDetails.timeIn;
                        timeIn = timeIn ? moment(timeIn).format("HH:mm:ss") : "00:00:00";
                        let timeOut = visitDetails.EndTime || visitDetails.timeOut;
                        timeOut = timeOut ? moment(timeOut).format("HH:mm:ss") : "00:00:00";
                        const newStart = moment(event.event.start).format("YYYY-MM-DD");
                        const newEnd = moment(event.event.end).format("YYYY-MM-DD");
                        this.rescheduleVisitHomeCare(
                            event,
                            visitDetails,
                            `${newStart}T${timeIn}`,
                            `${newEnd}T${timeOut}`
                        );
                    } else {
                        this.rescheduleVisit(visitDetails, moment(event.event.start).format("MM/DD/YYYY"));
                    }
                    this.filterDuplicateEvent(event);
                },

                eventContent: (arg: EventContentArg) => {
                    const eventData = arg?.event?.extendedProps;
                    let extraEvents = 0;

                    if (eventData?.isExtraEvent) {
                        let startDate = new Date(Date.parse(eventData?.StartDate));
                        let key = `${eventData.PatientId}${startDate.toLocaleDateString()}`;
                        let currentTaskList = this.tasksMap.get(key);
                        extraEvents = currentTaskList.length - this.eventLimit;
                    }
                    return {
                        html: this.generateEvent(arg, extraEvents)
                    };
                },

                eventDidMount: (arg: any) => {
                    const self = this;
                    const scheduledEvent = arg?.event?.extendedProps;
                    const reassignBtn = `btn-reassign-${scheduledEvent.Id}`;
                    const postToAxxessCareBtn = `btn-posttocare-${scheduledEvent.Id}`;
                    const visitDetailsBtn = `btn-visitdetails-${scheduledEvent.Id}`;
                    const evvHouse = `evv-house-${scheduledEvent.Id}`;
                    const yellowNote = `yellow-note-${scheduledEvent.Id}`;
                    const orangeNote = `orange-note-${scheduledEvent.Id}`;
                    const redNote = `red-note-${scheduledEvent.Id}`;
                    const yellowTooltip = `yellow-tooltip-${scheduledEvent.Id}`;
                    const orangeTooltip = `orange-tooltip-${scheduledEvent.Id}`;
                    const redTooltip = `red-tooltip-${scheduledEvent.Id}`;
                    const deleteVisitBtn = `btn-deletevisit-${scheduledEvent.Id}`;
                    const restoreVisitBtn = `btn-restorevisit-${scheduledEvent.Id}`;

                    const hidePopover = () => $(arg?.el).popover("hide");
                    const showPopover = () => {
                        self.attachTooltip(arg);
                        $(arg?.el).popover("show");
                    };
                    const registerNoteHover = (noteId: string, color: CommentColorEnum) => {
                        const noteE1 = document.getElementById(noteId);
                        if (!noteE1 || noteE1.dataset.hoverRegistered) {
                            return;
                        }
                        noteE1.addEventListener("mouseenter", (event: MouseEvent) => {
                            event.stopPropagation();
                            event.preventDefault();
                            hidePopover();
                            self.attachVisitTooltip(arg, color);
                        });
                        noteE1.addEventListener("mouseleave", () => {
                            $(`.visit-${color}-note[role="tooltip"]`)?.remove();
                            showPopover();
                        });
                        noteE1.dataset.hoverRegistered = "true";
                    };
                    $('[role="tooltip"]')?.remove();
                    self.attachTooltip(arg);

                    if (!!document.getElementById(reassignBtn)) {
                        document.getElementById(reassignBtn).addEventListener("click", () => {
                            // adding this click event listener manually because calling onclick event directly causes issue since DOM loads first before the JS.
                            self.scheduledVisit(arg);
                        });
                    }
                    if (!!document.getElementById(evvHouse)) {
                        document.getElementById(evvHouse).addEventListener("click", () => {
                            self.openEvvHouse(arg);
                        });
                    }

                    if (!!document.getElementById(orangeNote)) {
                        document.getElementById(orangeNote).addEventListener("click", () => {
                            self.openVisitNote(arg, CommentColorEnum.orange);
                        });
                    }
                    if (!!document.getElementById(redNote)) {
                        document.getElementById(redNote).addEventListener("click", () => {
                            self.openVisitNote(arg, CommentColorEnum.red);
                        });
                    }

                    if (!!document.getElementById(yellowNote)) {
                        document.getElementById(yellowNote).addEventListener("click", () => {
                            self.openVisitNote(arg, CommentColorEnum.yellow);
                        });
                    }

                    if (!!document.getElementById(postToAxxessCareBtn)) {
                        document.getElementById(postToAxxessCareBtn).addEventListener("click", () => {
                            self.showPostToAxxessCare(arg);
                        });
                    }

                    if (!!document.getElementById(visitDetailsBtn)) {
                        document.getElementById(visitDetailsBtn).addEventListener("click", () => {
                            self.showVisitDetails(arg);
                            this._analyticsService.logEvent({
                                category: AnalyticsCategory.CentralVisitActions,
                                action: "Central Patient calendar Task Details"
                            });
                        });
                    }

                    if (!!document.getElementById(deleteVisitBtn)) {
                        document.getElementById(deleteVisitBtn).addEventListener("click", () => {
                            self.deleteTask(arg);
                            this._analyticsService.logEvent({
                                category: AnalyticsCategory.CentralVisitActions,
                                action: "Central Patient calendar Delete Task"
                            });
                        });
                    }

                    if (!!document.getElementById(restoreVisitBtn)) {
                        document.getElementById(restoreVisitBtn).addEventListener("click", () => {
                            self.initiateRestoreTask(arg);
                        });
                    }

                    registerNoteHover(yellowTooltip, CommentColorEnum.yellow);
                    registerNoteHover(orangeTooltip, CommentColorEnum.orange);
                    registerNoteHover(redTooltip, CommentColorEnum.red);

                    // click event listener for edit icon
                    const editButtonId = `edit-btn-${scheduledEvent.Id}`;
                    if (!!document.getElementById(editButtonId)) {
                        document.getElementById(editButtonId).addEventListener("click", () => {
                            $('[role="tooltip"]')?.remove();

                            self.tasksList.forEach((task: any) => {
                                const selectorElement = $(`#scheduled-event-${task.Id}`).closest("a")[0];
                                if (selectorElement) {
                                    if (task.Id != scheduledEvent.Id) {
                                        selectorElement.style.zIndex = "0";
                                    } else {
                                        selectorElement.style.zIndex = "5";
                                    }
                                }
                            });
                        });
                    }

                    // Permission Logic
                    const editBtnId = `edit-icon-${scheduledEvent.Id}`;
                    let showEditButtonForVisit: boolean = false;
                    let showReassignButtonForVisit: boolean = false;
                    showReassignButtonForVisit =
                        self.isReassignAllowed &&
                        (scheduledEvent.Status == VisitStatusEnum.NotStarted ||
                            scheduledEvent.Status == VisitStatusEnum.NotYetDue) &&
                        scheduledEvent.EvvIconStatus !== EvvStatusHomeCareEnum.Finished &&
                        scheduledEvent.EvvIconStatus !== EvvStatusHomeCareEnum.FinishedAdjusted &&
                        scheduledEvent.EvvIconStatus !== EvvStatusHomeCareEnum.InProgress &&
                        scheduledEvent.EvvIconStatus !== EvvStatusHomeCareEnum.InProgressAdjusted &&
                        scheduledEvent.VerificationVisitStatus !== EvvStatusHomeHealthEnum.PatientVerified &&
                        scheduledEvent.VerificationVisitStatus !== EvvStatusHomeHealthEnum.ClinicianVerified;
                    let showVisitDetailsButtonForVisit: boolean = false;
                    showVisitDetailsButtonForVisit =
                        self.isEditVisitDetailsAllowed &&
                        (scheduledEvent.Application == ApplicationEnum.AgencyCore ||
                            scheduledEvent.Application == ApplicationEnum.HomeCare ||
                            scheduledEvent.Application == ApplicationEnum.AxxessPalliative ||
                            scheduledEvent.Application == ApplicationEnum.AxxessHospiceFE);
                    showEditButtonForVisit =
                        self.showEditBtn && (showReassignButtonForVisit || showVisitDetailsButtonForVisit);
                    if (!!document.getElementById(editBtnId)) {
                        if (!showEditButtonForVisit) {
                            document.getElementById(editBtnId).style.display = "none";
                            return;
                        }

                        if (!!document.getElementById(reassignBtn) && !showReassignButtonForVisit) {
                            document.getElementById(reassignBtn).style.display = "none";
                        }
                        if (!!document.getElementById(visitDetailsBtn) && !showVisitDetailsButtonForVisit) {
                            document.getElementById(visitDetailsBtn).style.display = "none";
                        }
                    }
                },

                eventWillUnmount: (arg: any) => {
                    let scheduledEvent = arg?.event?.extendedProps;
                    let reassignBtn = `btn-reassign-${scheduledEvent.Id}`;
                    if (!!document.getElementById(reassignBtn)) {
                        document.getElementById(reassignBtn).removeEventListener("click", () => {
                            console.log("Removed Reassign button Click Event Listener");
                        });
                    }

                    let editButtonId = `edit-btn-${scheduledEvent.Id}`;
                    if (!!document.getElementById(editButtonId)) {
                        document.getElementById(editButtonId).removeEventListener("click", () => {
                            console.log("Removed Edit Button Click Event Listener");
                        });
                    }
                },
                // NOTE: Uncomment when task navigation is needed
                // eventClick: (event: any) => {
                //     if (!$(event.el).find(".more-btn").length) {
                //         this.handleEventClick(event);
                //     }
                // },
                resourceLabelContent: (arg: any) => {
                    $('[role="tooltip"]')?.remove();
                    return {
                        html: this.generateResource(arg)
                    };
                },
                resourceLabelDidMount: (arg: any) => {
                    this.registerClick(arg);
                },
                resourceGroupLabelContent: (arg: any) => {
                    return {
                        html: this.generateGroupLabel(arg)
                    };
                },
                visibleRange: {
                    start: this.startDate,
                    end: adjustDate(this.endDate, 1)
                }
            };
        }
    },

    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        generateGroupLabel(arg: any) {
            let groupId = arg?.groupValue;
            let employeeName = this.userNamesMap?.get(groupId);
            let labelContent = "";
            labelContent = `<span class="text-dark fw-bold"> ${employeeName} </span>`;
            return labelContent;
        },
        async rescheduleVisitHomeCare(dropEvent: any, event: any, StartDate: string, EndDate: string) {
            const { Id, AgencyId, Application, UserId, EpisodeId, PatientId, TimeIn, TimeOut } = event as IVisitDetails;
            const rescheduleDetails: IReassignCalendarVisitHC = {
                AgencyId,
                PatientId,
                EventId: Id,
                EpisodeId,
                Application,
                UserId,
                TimeIn,
                TimeOut,
                VisitStartTime: StartDate,
                VisitEndTime: EndDate,
                isDragDrop: true,
                Comments: this.getComment(event)
            };
            this.showLoading(true);

            try {
                const firstResponse = await this._taskService.rescheduleTaskHC(rescheduleDetails);

                if (!firstResponse.isSuccessful && firstResponse.errorDetails) {
                    this.showLoading(false);
                    if (!firstResponse.errorDetails.taskIssues.length) {
                        throw new Error(firstResponse.errorMessage);
                    }

                    const validationList: IRescheduleValidations[] = firstResponse.errorDetails.taskIssues.map(
                        (item: any) => {
                            return {
                                title: item.title as string,
                                description: [item.message] as string[],
                                taskIssueSurpressKey: item.taskIssueSurpressKey as string
                            };
                        }
                    );

                    const hasError = firstResponse.errorDetails.allTasksHaveErrors;

                    this.dialogRef = this.$dialog.open(RescheduleValidation, {
                        props: {
                            modal: true,
                            closeOnEscape: false,
                            showHeader: false,
                            style: {
                                width: "60vw"
                            }
                        },

                        data: {
                            validations: validationList,
                            name: "Patient",
                            component: "Schedule-visit",
                            hasError
                        },

                        onClose: async (options) => {
                            if (options.data == "cancel") {
                                return;
                            } else {
                                const issuesResponseObj: IHandleTaskReschuleIssue = {};
                                for (let issue of validationList) {
                                    issuesResponseObj[issue.taskIssueSurpressKey] = "ScheduleAllAnyways";
                                }

                                rescheduleDetails.HandleTaskIssues = issuesResponseObj;
                                const secondResponse = await this._taskService.rescheduleTaskHC(rescheduleDetails);
                                if (!secondResponse.isSuccessful) {
                                    throw new Error(secondResponse.errorMessage);
                                }

                                if (secondResponse.isSuccessful) {
                                    this._toastrService.success({
                                        title: `Visit Scheduled`,
                                        message: `Visit has been successfully Scheduled.`
                                    });

                                    this.$emit("calendarRefresh");
                                }
                            }
                        }
                    });
                }

                if (firstResponse.isSuccessful && !firstResponse.errorDetails) {
                    this._toastrService.success({
                        title: `Success`,
                        message: `The visit has been successfully rescheduled.` // home care
                    });
                    this.$emit("calendarRefresh");
                    this.showLoading(false);
                }
            } catch (error) {
                this._toastrService.error({
                    title: `Error - Rescheduling task`,
                    message: error.message || `There was an error rescheduling the visit. Please Try again later.`
                });
            } finally {
                this.$emit("calendarRefresh");
                this.showLoading(false);
            }
        },

        async rescheduleVisitHosPal(event: IVisitDetails, StartDate: string, EndDate: string) {
            const { Id, AgencyId, Application, UserId } = event;
            const rescheduleDetails: IRescheduleVisitHosPal = {
                TaskId: Id,
                AccountId: AgencyId,
                StartDate,
                EndDate,
                UserId,
                Application
            };
            this.showLoading(true);
            try {
                await this._taskService.rescheduleTaskHosPal(rescheduleDetails);
                this._toastrService.success({
                    title: `Success`,
                    message: `The visit has been successfully rescheduled.`
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error - Rescheduling task`,
                    message: `There was an error rescheduling the visit. Please Try again later.`
                });
            } finally {
                this.$emit("calendarRefresh");
                this.showLoading(false);
            }
        },

        async rescheduleVisit(event: any, date: string) {
            const visitDetails = event as IVisitDetails;
            const rescheduleVisitDetails = new RescheduleTask();
            rescheduleVisitDetails.fillRequiredDetails(visitDetails);
            rescheduleVisitDetails.EventDate = rescheduleVisitDetails.VisitDate = date || null;
            rescheduleVisitDetails.Comments = this.getComment(event);

            if (rescheduleVisitDetails.EventDate) {
                this.showLoading(true);
                try {
                    const response = await this._taskService.rescheduleTask(rescheduleVisitDetails);
                    if (response?.isSuccessful) {
                        this._toastrService.success({
                            title: `Success`,
                            message: `The visit has been successfully rescheduled.`
                        });
                    } else {
                        throw new Error();
                    }
                } catch (error) {
                    this._toastrService.error({
                        title: `Error - Rescheduling task`,
                        message: `There was an error rescheduling the visit. Please Try again later.`
                    });
                } finally {
                    this.$emit("calendarRefresh");
                    this.showLoading(false);
                }
            } else {
                this._toastrService.error({
                    title: `Error - Rescheduling task`,
                    message: `There was an error rescheduling the visit. Please Try again later.`
                });

                this.$emit("calendarRefresh");
            }
        },

        async showPostToAxxessCare(arg: any) {
            const taskDetails = arg?.event?.extendedProps as IVisitDetails;
            const visitDetails: IVisitDetails = Object.assign({}, taskDetails);

            this.dialogRef = this.$dialog.open(PostToAxxesscare, {
                props: {
                    modal: true,
                    showHeader: false,
                    contentStyle: {
                        width: "700px",
                        overflowX: "hidden"
                    }
                },
                data: {
                    visitDetails
                },
                onClose: (response: any) => {
                    this.$emit("calendarRefresh");
                }
            });
        },

        async showVisitDetails(arg: any) {
            const taskDetails = arg?.event?.extendedProps as IVisitDetails;
            const visitDetails: IVisitDetails = Object.assign({}, taskDetails);
            this.dialogRef = this.$dialog.open(VisitDetails, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    visitDetails: visitDetails
                },
                onClose: (response: any) => {
                    if (response.data === "success") {
                        this.$emit("calendarRefresh");
                    }
                }
            });
        },

        async deleteTask(args: any) {
            const events = args?.event?.extendedProps as IVisitDetails;
            try {
                let promptOptions: IPromptOptions = {
                    message: "Are you sure you want to delete selected task?",
                    okText: "Yes",
                    cancelText: "No"
                };
                const eventId = [events.Id] as string[];

                this.dialogRef = this.$dialog.open(Prompt, {
                    props: {
                        modal: true,
                        contentStyle: {
                            overflowX: "hidden"
                        },
                        showHeader: false
                    },

                    data: {
                        promptOptions
                    },

                    onClose: async (options: any) => {
                        if (options.data == "success") {
                            this.showLoading(true);
                            try {
                                if (events.Application == ApplicationEnum.AgencyCore) {
                                    const deleteTaskPayload: IDeleteTaskPayload = {
                                        AgencyId: events.AgencyId,
                                        Application: events.Application,
                                        PatientId: events.PatientId,
                                        EpisodeId: events.EpisodeId,
                                        EventId: events.Id,
                                        UserId: events.UserId
                                    };
                                    const res = await this._taskService.deleteTask(deleteTaskPayload);
                                    if (!res?.isSuccessful) {
                                        this.showLoading(false);
                                        this._toastrService.error({
                                            title: `Error - Deleting task`,
                                            message: res?.message
                                                ? res?.message
                                                : `There was an error deleting the visit. Please Try again later.`
                                        });
                                        return;
                                    }
                                } else {
                                    const deleteTaskPayload: IBulkDeletePayload = {
                                        AgencyId: events.AgencyId,
                                        Application: events.Application,
                                        PatientId: events.PatientId,
                                        EventId: eventId,
                                        IsPatient: true
                                    };
                                    const res = await this._taskService.patientBulkDelete(deleteTaskPayload);
                                    if (!res?.isSuccessful) {
                                        throw Error;
                                    }
                                }
                                this.$emit("calendarRefresh");
                                this.showLoading(false);
                                this._toastrService.success({
                                    message: `The task has been successfully deleted.`,
                                    title: `Task Deleted`
                                });
                            } catch (error) {
                                this.showLoading(false);
                                this._toastrService.error({
                                    title: `Error - Deleting task`,
                                    message: `There was an error deleting the visit. Please Try again later.`
                                });
                            }
                        }
                    }
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error - Task`,
                    message: `There was a problem while deleting the tasks. Please try again.`
                });
            }
        },

        async initiateRestoreTask(args: any) {
            const events = args?.event?.extendedProps as IVisitDetails;
            try {
                const promptOptions: IPromptOptions = {
                    message: "Are you sure you want to restore selected task?",
                    okText: "Yes",
                    cancelText: "No"
                };
                const eventId = [events.Id] as string[];

                this.dialogRef = this.$dialog.open(Prompt, {
                    props: {
                        modal: true,
                        contentStyle: {
                            overflowX: "hidden"
                        },
                        showHeader: false
                    },

                    data: {
                        promptOptions
                    },

                    onClose: async (options: any) => {
                        if (options.data == "success") {
                            await this.restoreMissedVisit(events);
                        }
                    }
                });
            } catch (error) {
                this._toastrService.error({
                    title: `Error - Task`,
                    message: `There was a problem while restoring the tasks. Please try again.`
                });
            }
        },

        async restoreMissedVisit(events: IVisitDetails) {
            try {
                const payload: IRestoreMissedVisitRequest = {
                    AgencyId: events.AgencyId,
                    EventId: events.Id,
                    Application: events.Application,
                    UserId: events.UserId
                };
                if (events.Application == ApplicationEnum.AgencyCore) {
                    payload.EpisodeId = events.EpisodeId;
                    payload.PatientId = events.PatientId;
                } else if (events.Application == ApplicationEnum.HomeCare) {
                    payload.PatientId = events.PatientId;
                }
                this.showLoading(true);
                const firstResponse = await this._taskService.restoreMissedVisit(payload);
                if (!firstResponse.IsSuccessful && firstResponse.ErrorDetails) {
                    if (!firstResponse.ErrorDetails.TaskIssues.length) {
                        throw new Error(firstResponse.ErrorMessage);
                    }

                    const validationList: IRescheduleValidations[] = firstResponse.ErrorDetails.TaskIssues.map(
                        (item: any) => {
                            return {
                                title: item.Title as string,
                                description: [item.Message] as string[],
                                taskIssueSurpressKey: item.TaskIssueSurpressKey as string
                            };
                        }
                    );

                    const hasError = firstResponse.ErrorDetails.AllTasksHaveErrors;

                    this.showLoading(false);
                    this.dialogRef = this.$dialog.open(RescheduleValidation, {
                        props: {
                            modal: true,
                            closeOnEscape: false,
                            showHeader: false,
                            style: {
                                width: "60vw"
                            }
                        },

                        data: {
                            validations: validationList,
                            name: "Patient",
                            component: "Restore-visit",
                            hasError
                        },

                        onClose: async (options: any) => {
                            if (options.data == "cancel") {
                                return;
                            } else {
                                const issuesResponseObj: IHandleTaskReschuleIssue = {};
                                for (let issue of validationList) {
                                    issuesResponseObj[issue.taskIssueSurpressKey] = "ScheduleAllAnyways";
                                }

                                this.showLoading(true);
                                const secondResponse = await this._taskService.restoreMissedVisit({
                                    ...payload,
                                    handleTaskIssues: issuesResponseObj
                                });
                                if (!secondResponse.IsSuccessful) {
                                    throw new Error(secondResponse.ErrorMessage);
                                } else {
                                    this.showLoading(false);
                                    this.$emit("calendarRefresh");
                                    this._toastrService.success({
                                        message: `The task has been successfully restored.`,
                                        title: `Task Restored`
                                    });
                                }
                            }
                        }
                    });
                } else if (!firstResponse.IsSuccessful) {
                    throw new Error(firstResponse.ErrorMessage);
                } else {
                    this.showLoading(false);
                    this.$emit("calendarRefresh");
                    this._toastrService.success({
                        message: `The task has been successfully restored.`,
                        title: `Task Restored`
                    });
                }
            } catch (error) {
                this.showLoading(false);
                this._toastrService.error({
                    title: `Error - Restoring task`,
                    message: error ? error : `There was an error restoring the visit. Please Try again later.`
                });
            }
        },

        async scheduledVisit(arg: any) {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralVisitActions,
                action: "Central Patient calendar reassign"
            });
            const taskDetails = arg?.event?.extendedProps;
            const patientDetails = this.patientsList.find(
                (patient: PatientCalendarModel) => taskDetails.PatientId == patient.Id
            );
            const patientInfo = (({ AgencyName, FirstName, LastName, Mrn }) => ({
                AgencyName,
                FirstName,
                LastName,
                Mrn
            }))(patientDetails);
            let visitDetails: IReassignVisitDetails = Object.assign({}, patientInfo, taskDetails);
            this.dialogRef = this.$dialog.open(ReassignVisit, {
                props: {
                    modal: true,
                    showHeader: false,
                    contentStyle: {
                        minWidth: "650px",
                        minHeight: visitDetails.Application === ApplicationEnum.HomeCare ? "350px" : "280px"
                    }
                },
                data: {
                    visitDetails
                },
                onClose: async (options: any) => {
                    if (options.data === "success") {
                        this.$emit("calendarRefresh");
                    }
                }
            });
        },

        showPostToAxxessCareBtn(event: IVisitDetails): boolean {
            let allowedApplications = [ApplicationEnum.AgencyCore, ApplicationEnum.HomeCare];
            if (!allowedApplications.includes(event.Application)) {
                return false;
            }
            let today = moment().startOf("day");

            let visitDate: moment.Moment;
            let shouldShow: boolean;

            if (event.Application === ApplicationEnum.AgencyCore) {
                visitDate = moment(event.VisitDate);
                shouldShow =
                    event.ShowPostToAxxessCareLink &&
                    (event.Status === "Not Yet Started" || event.Status === "Not Yet Due") &&
                    (visitDate.isSame(today, "day") || visitDate.isAfter(today));
            } else if (event.Application === ApplicationEnum.HomeCare && this.showPostToAxxessCareTest) {
                visitDate = moment(event.StartDate);
                shouldShow =
                    event.ShowPostToAxxessCareLink &&
                    (event.Status === "Not Yet Started" || event.Status === "Not Yet Due") &&
                    (visitDate.isSame(today, "day") || visitDate.isAfter(today));
            } else {
                return false;
            }
            return shouldShow;
        },

        axxessCareHeart(event: IVisitDetails) {
            if (event?.IsAxxessCareNote) {
                if (event.PrivateJob) {
                    return `<div class="axxesscare-heart">
                <i
                    class="fa fa-heart axcare-internal-job-color"
                    tooltip="<b>Internal visit</b> (Visible only to your staff clinicians)"
                ></i>
                </div>
            `;
                } else {
                    return `<div class="axxesscare-heart">
                <i
                    class="fa fa-heart axcare-external-job-color"
                    tooltip="<b>External visit</b> (Visible to all qualified clinicians nearby)"
                 ></i>
                </div>
            `;
                }
            } else {
                return "";
            }
        },

        generateEvent(arg: EventContentArg, extraEvents: number) {
            const event = arg?.event?.extendedProps as IVisitDetails;
            const date = new Date(event.EndDate);
            const day = date.getDay();
            const saturday = 6;
            let eventContent = "";
            const eventColor: string =
                !event.UserFirstName && !event.UserLastName ? "warning" : ScheduleLegendState[event.State];
            const showPostToAxxessCareBtn: boolean = this.showPostToAxxessCareBtn(event) || false;
            const showDeleteBtn: boolean =
                event.Status == VisitStatusEnum.NotStarted || event.Status == VisitStatusEnum.NotYetDue;
            let eventStatus = "";
            let eventClass = "";
            let statusName = "";
            if (
                (event.Application === ApplicationEnum.AxxessHospiceFE ||
                    event.Application === ApplicationEnum.AxxessPalliative) &&
                event.State === ScheduleLegendState.danger
            ) {
                statusName = `Missed Visit (${event.Status})`;
            } else {
                statusName = `${event.Status}`;
            }
            if (event.VisitConfirmed) {
                eventStatus = `<i class="fa fa-check-circle" aria-hidden="true"></i> Confirmed`;
                eventClass = "confirmed";
            } else if (event.PatientCalled) {
                eventStatus = `<i class="fa fa-exclamation-circle" aria-hidden="true"></i> Reschedule`;
                eventClass = "reschedule"; // using 'PatientCalled' temporary until we get veriable for "Call To Reschedule" from Solution
            } else if (event.RescheduleVisit) {
                eventClass = "reschedule";
                eventStatus = `<i class="fa fa-exclamation-circle" aria-hidden="true"></i> Reschedule`;
            }
            let isEventstatus = false;
            if (event.VisitConfirmed || event.PatientCalled || event.RescheduleVisit) {
                isEventstatus = true;
            }
            let evvHouse = null;
            if (event.Application == ApplicationEnum.HomeCare) {
                if (
                    event.EvvIconStatus === EvvStatusHomeCareEnum.Finished ||
                    event.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                ) {
                    evvHouse =
                        event.EvvIconStatus === EvvStatusHomeCareEnum.FinishedAdjusted
                            ? `<a class='evv ps-1'><i class='evv-adjusted signed'></i></a>`
                            : `<a class='evv ps-1'><i class='fa fa-home fa-md signed'></i></a>`;
                } else if (
                    event.EvvIconStatus === EvvStatusHomeCareEnum.InProgress ||
                    event.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                ) {
                    evvHouse =
                        event.EvvIconStatus === EvvStatusHomeCareEnum.InProgressAdjusted
                            ? `<a class='evv ps-1'><i class='evv-adjusted in-progress'></i></a>`
                            : `<a class='evv ps-1'><i class='fa fa-home fa-md'></i></a>`;
                } else if (event.EvvIconStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    evvHouse = `<a class='evv gray-house ps-1'><i class='fa fa-home fa-md not-started'></i></a>`;
                }
            } else if (event.Application == ApplicationEnum.AgencyCore) {
                if (event.VerificationVisitStatus === EvvStatusHomeHealthEnum.InProgress) {
                    evvHouse = `<a class='evv ps-1'><i class='fa fa-home fa-md'></i></a>`;
                } else if (
                    event.VerificationVisitStatus === EvvStatusHomeHealthEnum.PatientVerified ||
                    event.VerificationVisitStatus === EvvStatusHomeHealthEnum.ClinicianVerified
                ) {
                    evvHouse = `<a class='evv ps-1'><i class='fa fa-home fa-md signed'></i></a>`;
                } else if (event.VerificationVisitStatus === EvvStatusHomeHealthEnum.NotStarted) {
                    evvHouse = `<a class='evv gray-house ps-1'><i class='fa fa-home fa-md not-started'></i></a>`;
                }
            }

            const note = event.Comments?.find((comment: any) => comment.CommentColor === "Blue");
            const alreadyExist = this.blueNote.some(
                (blnote) => blnote.id == event.PatientId && blnote.message == note?.Comment
            );
            if (note && !alreadyExist) {
                this.blueNote.push({
                    id: event.PatientId,
                    element: `<span id=${`blue-note-${event.PatientId}`}><i id=${`blue-tooltip-${event.PatientId}`} class="me-2 fas fa-sticky-note blue-note cursor-pointer"></i></span>`,
                    message: note.Comment
                });
            }
            let yellowNote = null as any;
            let redNote = null as any;
            let orangeNote = null as any;
            event.Comments?.map((comment: any) => {
                if (comment.CommentColor === "Yellow") {
                    yellowNote = {
                        id: event.PatientId,
                        element: `<i id=${`yellow-tooltip-${event.Id}`} class="me-2 fas fa-sticky-note yellow-note cursor-pointer"></i>`
                    };
                }
                if (comment.CommentColor === "Red") {
                    redNote = {
                        id: event.PatientId,
                        element: `<i id=${`red-tooltip-${event.Id}`} class="me-2 fas fa-sticky-note red-note  cursor-pointer"></i>`
                    };
                }
                if (comment.CommentColor === "Orange") {
                    orangeNote = {
                        id: event.PatientId,
                        element: `<i id=${`orange-tooltip-${event.Id}`} class="me-2 fas fa-sticky-note orange-note  cursor-pointer"></i>`
                    };
                }
            });
            let showRestoreButton = false;
            if (event.Application === ApplicationEnum.AgencyCore || event.Application === ApplicationEnum.HomeCare) {
                showRestoreButton = event.State === 3;
            } else if (
                (event.Application === ApplicationEnum.AxxessPalliative ||
                    event.Application === ApplicationEnum.AxxessHospice ||
                    event.Application === ApplicationEnum.AxxessHospiceFE) &&
                event.ShowRestoreMissedVisit
            ) {
                const isLoggedInUsersTask =
                    this.agencyList?.findIndex(
                        (agency) => agency.application === event.Application && agency.userId === event.UserId
                    ) !== -1;
                showRestoreButton = isLoggedInUsersTask;
            }
            if (!!event) {
                eventContent = `<div class="ax-calendar-event ${eventColor}" id = "scheduled-event-${event.Id}">
                <span class="w-100 d-flex justify-content-between">
                    <span class="fw-bold text-truncate font-size-xs">
                            ${event.TaskName}
                    </span>
                    <span class="btn-group table-btn-group ms-auto" id = "edit-icon-${event.Id}">
                        <button type="button" class="btn btn-xs px-1 py-0 btn-outline-primary dropdown-toggle" data-bs-toggle="dropdown" id = "edit-btn-${
                            event.Id
                        }">
                            <i class="fas fa-ellipsis-h"></i>
                        </button>
                        <div class=" dropdown-menu ${day === saturday ? "" : "dropdown-transform-right mt-4"}">
                            <a
                                class="dropdown-item"
                                href="javascript:void(0);"
                                id = "btn-reassign-${event.Id}"
                            >
                            Reassign
                            </a>
                            ${
                                showPostToAxxessCareBtn
                                    ? `<a
                                    class="dropdown-item"
                                    href="javascript:void(0);"
                                    id="btn-posttocare-${event.Id}"
                                >
                                Post To AxxessCare
                                </a>`
                                    : ""
                            }
                            <a
                                class="dropdown-item"
                                href="javascript:void(0);"
                                id = "btn-visitdetails-${event.Id}"
                            >
                            Details
                            </a>
                            ${
                                showDeleteBtn
                                    ? `<a
                                  class="dropdown-item"
                                  href="javascript:void(0);"
                                  id="btn-deletevisit-${event.Id}"
                                >
                                Delete
                                </a>`
                                    : ""
                            }
                            ${
                                showRestoreButton
                                    ? `<a
                                  class="dropdown-item"
                                  href="javascript:void(0);"
                                  id="btn-restorevisit-${event.Id}"
                                >
                                Restore
                                </a>`
                                    : ""
                            }
                        </div>
                    </span>
                </span>
                    <span class="w-100 font-size-3xs text-truncate ${event.StartTime ? `padding-top` : ``}">
                        ${
                            event.Application === ApplicationEnum.HomeCare ||
                            event.Application === ApplicationEnum.AgencyCore
                                ? formatScheduledTime(event.StartTime) !== formatScheduledTime(event.EndTime)
                                    ? formatTimeInOut(
                                          formatScheduledTime(event.StartTime),
                                          formatScheduledTime(event.EndTime)
                                      )
                                    : ""
                                : formatTimeInOut(
                                      formatScheduledTime(event.StartTime),
                                      formatScheduledTime(event.EndTime)
                                  )
                        }
                    </span>
                    <span class="w-100 font-size-3xs text-truncate padding-top ">
                        <span> ${statusName} </span>
                    </span>
                    <span class="d-flex align-items-center font-size-2xs w-100 justify-content-between padding-top fw-semibold">
                        <span>
                            <i class="fas fa-user me-1"></i>
                            <span>
                                ${this.getUserName(event.UserFirstName, event.UserLastName)}
                            </span>
                        </span>
                        <span class="d-flex" id='evv-house-${event.Id}'>
                            ${this.axxessCareHeart(event)}
                             ${evvHouse ? evvHouse : ""}
                        </span>
                   </span>
                   <span class="d-flex">
                   ${
                       ((event.Application === ApplicationEnum.AxxessHospice ||
                           event.Application === ApplicationEnum.AxxessHospiceFE) &&
                           !this.canViewTaskNotesHos) ||
                       (event.Application === ApplicationEnum.AxxessPalliative && !this.canViewTaskNotesPal)
                           ? ""
                           : `<span id='yellow-note-${event.Id}'>
                        ${yellowNote ? yellowNote.element : ""}
                    </span>`
                   }
                     <span id='red-note-${event.Id}'>
                        ${redNote ? redNote.element : ""}
                    </span>
                     <span id='orange-note-${event.Id}'>
                        ${orangeNote ? orangeNote.element : ""}
                    </span>
                    </span>
                    <span class="d-flex w-100 justify-content-between align-items-center ${
                        isEventstatus || event?.OnCall || event.PRN ? `padding-top` : ``
                    }">
                        ${
                            isEventstatus
                                ? `<span class="status-${eventClass} fw-semibold">
                                ${eventStatus}
                            </span>`
                                : ``
                        }
                        ${event?.OnCall ? `<span class="pill pill-oncall"> On-Call </span>` : ``}
                        ${event.PRN ? `<span class="pill pill-prn"> PRN </span>` : ``}
                    </span>
                </div>`;
            }
            if (!!extraEvents) {
                eventContent = `<a class="cursor-pointer text-dark more-btn" tabindex="0">+ ${extraEvents} more</a>`;
            }
            return eventContent;
        },

        generateResource(arg: any) {
            const resource = arg?.resource?.extendedProps;
            let patientInfo = "";
            const phoneNumber = new FormatPhoneNumber().toView(resource.PhoneNumber?.toString());
            const isHomeCareAddAllowed = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.HomeCare
            );
            const isHosAddAllowed = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessHospiceFE
            );
            const isPalAddAllowed = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessPalliative
            );
            const isHomeHealthAddAllowed = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AgencyCore
            );

            if (!!resource) {
                const blueNote = this.blueNote.find((note: any) => note.id === resource.Id);
                let productClass = new GetProductClass();
                let application = resource.Application;
                if (application === ApplicationEnum.AxxessHospiceFE) {
                    application = ApplicationEnum.AxxessHospice;
                }
                patientInfo = `<div class="mx-2 d-flex flex-column align-items-start p-2">
                                <span class="fw-normal font-size-xs badge badge-pill pill me-1 ${productClass.toView(
                                    application
                                )}">
                                    ${ApplicationNameEnum[application]}
                                </span>
                                <div class="position-absolute bulk-reassign-wrapper">
                                        <i data-bs-toggle="dropdown" class="fas fa-ellipsis-h cursor-pointer all-actions" id="reassign-ellipsis-${resource.Id}"></i>
                                    <div class="dropdown-menu py-1" aria-labelledby="reassign-ellipsis-${resource.Id}">
                                        <a
                                            class="dropdown-item font-size-sm bulk-reassign"
                                            href="javascript:void(0);"
                                            id="task-manager-${resource.Id}">
                                                Task Manager
                                        </a>
                                        <a
                                            class="dropdown-item font-size-sm bulk-reassign"
                                            href="javascript:void(0);"
                                            id="profile-${resource.Id}">
                                                Profile
                                        </a>
                                         ${
                                             (application == ApplicationEnum.AxxessHospiceFE && isHosAddAllowed) ||
                                             (application == ApplicationEnum.AxxessHospice && isHosAddAllowed) ||
                                             (application == ApplicationEnum.AxxessPalliative && isPalAddAllowed) ||
                                             (application == ApplicationEnum.HomeCare && isHomeCareAddAllowed) ||
                                             (application == ApplicationEnum.AgencyCore && isHomeHealthAddAllowed)
                                                 ? ` <a
                                            class="dropdown-item font-size-sm bulk-reassign"
                                            href="javascript:void(0);"
                                            id="schedule-visits-${resource.Id}">
                                                Add Task
                                        </a>`
                                                 : ""
                                         }
                                    </div>
                                </div>
                                <a class="w-auto font-size-base text-truncate cursor-pointer patient-link" id="${
                                    resource.Id
                                }">
                                    ${formatName(
                                        resource.FirstName,
                                        resource.LastName,
                                        "LN, FN",
                                        "",
                                        resource.MiddleInitial
                                    )}
                                </a>
                                <span class="font-size-xs">${resource.Mrn}</span>
                                ${
                                    phoneNumber
                                        ? `<a href="tel:${phoneNumber}" class="mb-0 font-size-xs w-auto">${phoneNumber}</a>`
                                        : ""
                                }
                                <p class="w-100 mb-1 font-size-2xs text-truncate">${resource.AgencyName}</p>


                                ${blueNote ? blueNote.element : ""}

                            </div>`;
            }
            return patientInfo;
        },

        registerClick(arg: any) {
            const element = arg?.el?.className.split(" ").join(".");
            const extendedProps = arg?.resource?.extendedProps;
            $(`.${element}`).on("click", `#${extendedProps?.Id}`, (event: any) => {
                this.handlePatientNameClick(extendedProps);
            });
            $(`.${element}`).on("click", `#task-manager-${extendedProps?.Id}`, (event: any) => {
                this.openTaskManager(extendedProps);
            });
            $(`.${element}`).on("click", `#profile-${extendedProps?.Id}`, (event: any) => {
                this.openProfile(extendedProps);
            });
            $(`.${element}`).on("click", `#schedule-visits-${extendedProps?.Id}`, (event: any) => {
                this.scheduleNewVisits(extendedProps);
            });

            $(`.${element}`).on("mouseenter", `#blue-tooltip-${extendedProps?.Id}`, (event: any) => {
                event.preventDefault();
                this.attachBlueNoteTooltip(arg);
            });

            $(`.${element}`).on("click", `#blue-note-${extendedProps?.Id}`, (event: any) => {
                this.openBlueVisitNote(arg);
            });
        },

        getBlueVisitNoteTitle(application: ApplicationEnum) {
            if (application === ApplicationEnum.HomeCare) {
                return "Care Period Comments";
            }
            return "Episode Comments";
        },

        openVisitNote(arg: any, color: string) {
            let noteTitle = "";
            const application = arg?.event?.extendedProps.Application;
            switch (color) {
                case CommentColorEnum.yellow:
                    if (application === ApplicationEnum.HomeCare) {
                        noteTitle = "Visit Comments";
                    } else {
                        noteTitle = "Task Notes";
                    }
                    break;
                case CommentColorEnum.blue:
                    noteTitle = this.getBlueVisitNoteTitle(application);
                    break;
                case CommentColorEnum.red:
                    if (application === ApplicationEnum.AgencyCore) {
                        noteTitle = "Missed/Returned Note";
                    } else if (application === ApplicationEnum.HomeCare) {
                        noteTitle = "Missed Comments";
                    } else {
                        noteTitle = "Missed Note";
                    }
                    break;
                case CommentColorEnum.orange:
                    if (application === ApplicationEnum.HomeCare) {
                        noteTitle = "Returned Comments";
                    } else {
                        noteTitle = "Returned Note";
                    }
                    break;
            }
            const scheduledEvent = arg?.event?.extendedProps;
            let element = $(arg?.el).find(`#${color}-note-${scheduledEvent.Id}`);
            if (!!$(arg?.el).find(`#${color}-note-${scheduledEvent.Id}`).length) {
                let content = this.getNotePopContent(scheduledEvent, color);
                (element as any)?.popover({
                    html: true,
                    content: content,
                    title: `<div class="d-flex justify-content-between">
                    <span>${noteTitle}</span>
                    <i class="fas fa-times cursor-pointer local-popup-close" id="care-period-${scheduledEvent.Id}"></i>
                    </div>`,
                    trigger: "click",
                    placement: "right",
                    container: "body",
                    customClass: `visit-${color}-popup-note`,
                    sanitize: false
                });
            }
            (element as any).popover("show");
            setTimeout(() => {
                document.querySelectorAll(".local-popup-close").forEach((btn) => {
                    btn.addEventListener("click", () => {
                        element.popover("hide");
                    });
                });
            }, 10);
        },

        openBlueVisitNote(arg: any) {
            const scheduledEvent = arg?.resource?.extendedProps;
            const application = arg?.resource?.extendedProps.Application;
            let title = this.getBlueVisitNoteTitle(application);
            let element = $(arg?.el).find(`#blue-note-${scheduledEvent.Id}`);
            if (!!$(arg?.el).find(`#blue-note-${scheduledEvent.Id}`).length) {
                let content = this.getBlueVisitPopupContent(scheduledEvent);
                (element as any).popover({
                    html: true,
                    content: content,
                    title: `<div class="d-flex justify-content-between">
                    <span>${title}</span>
                    <i class="fas fa-times cursor-pointer local-popup-close" id="care-period-${scheduledEvent.Id}"></i>
                    </div>`,
                    trigger: "click",
                    placement: "right",
                    container: "body",
                    customClass: "visit-blue-popup-note",
                    sanitize: false
                });
            }
            (element as any).popover("show");
            setTimeout(() => {
                document.querySelectorAll(".local-popup-close").forEach((btn) => {
                    btn.addEventListener("click", () => {
                        element.popover("hide");
                    });
                });
            }, 10);
        },

        attachVisitTooltip(arg: any, color: string) {
            const scheduledEvent = arg?.event?.extendedProps;
            if (!!$(arg?.el).find(`#${color}-tooltip-${scheduledEvent.Id}`).length) {
                let element = $(arg?.el).find(`#${color}-tooltip-${scheduledEvent.Id}`);
                let content = this.getNoteTooltipContent(arg?.event?.extendedProps, color);
                (element as any).tooltip({
                    html: true,
                    title: content,
                    container: "body",
                    customClass: `visit-${color}-note`
                });

                (element as any).tooltip("show");
            }
        },

        attachBlueNoteTooltip(arg: any) {
            const scheduledEvent = arg?.resource?.extendedProps;
            if (!!$(arg?.el).find(`#blue-tooltip-${scheduledEvent.Id}`).length) {
                let element = $(arg?.el).find(`#blue-tooltip-${scheduledEvent.Id}`);
                let content = this.getBlueVisitTooltipContent(scheduledEvent);
                (element as any).tooltip({
                    html: true,
                    title: content,
                    container: "body",
                    customClass: "visit-blue-note"
                });
                (element as any).tooltip("show");
            }
        },

        getNoteTooltipContent(data: any, color: string) {
            if (color === CommentColorEnum.red || color === CommentColorEnum.orange) {
                const details = data.Comments.find(
                    (comment: any) => comment.CommentColor === color.charAt(0).toUpperCase() + color.slice(1)
                );
                let allComments =
                    details.CommentType === StickyNoteTypeEnum.MissedVisit
                        ? details.Comment
                        : this.getTaskReturnComments(details.MultiComments);
                const text = `<div class="visit-${color.toLowerCase()}-note">
                        <span style="display:block";>${allComments}</span>
                </div>`;
                return text;
            } else {
                const textList = data.Comments.find(
                    (comment: any) => comment.CommentColor === color.charAt(0).toUpperCase() + color.slice(1)
                );
                let text = textList && textList.MultiComments ? textList.MultiComments[0] : "";
                text = `**${textList?.Comment}**` === text ? text.slice(2, text.length - 2) : text;
                if (color === CommentColorEnum.yellow) {
                    text = this.formatAndTruncateMessage(text);
                }
                return `<div class="visit-${color.toLowerCase()}-note">${text}</div>`;
            }
        },

        formatAndTruncateMessage(text: string) {
            const [sender, message, timestamp] = text
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean);

            const truncatedMessage = truncateText(message, 50);

            return `${sender}<br>${truncatedMessage}<br>${timestamp}`;
        },

        getBlueVisitTooltipContent(content: any) {
            const element = this.blueNote.find((note: any) => note.id === content.Id);
            return `<div class="visit-blue-note">${element.message}</div>`;
        },

        getNotePopContent(content: any, color: string) {
            if (color === CommentColorEnum.red || color === CommentColorEnum.orange) {
                const details = content.Comments.find(
                    (comment: any) => comment.CommentColor === color.charAt(0).toUpperCase() + color.slice(1)
                );
                const text =
                    details.CommentType === StickyNoteTypeEnum.MissedVisit
                        ? `<span style="display:block";>${details.Comment}</span>`
                        : details.MultiComments?.map(
                              (comment: string) => `<span style="display:block";>${comment}</span>`
                          ).join("");
                return `<div class="visit-${color}-popup-note">${text}</div>`;
            } else {
                const textList = content.Comments.find(
                    (comment: any) => comment.CommentColor === color.charAt(0).toUpperCase() + color.slice(1)
                );
                const text = textList?.MultiComments?.map((comment: string) => {
                    const lines = comment.split("\n");
                    let displayLine = lines
                        ?.map((line, index) => {
                            if (lines.length === index + 1) {
                                if (`**${textList?.Comment}**` === line) {
                                    return `<span style="display:block";>${line.slice(2, line.length - 2)}</span>`;
                                }
                            }
                            if (index === 0) {
                                return `<span style="display:block; color:red">${line.trim()}</span>`;
                            } else if (index === 2) {
                                return `<span style="display:block; color:gray; font-size:10px">${line.trim()}</span>`;
                            }
                            return `<span style="display:block;">${line.trim()}</span>`;
                        })
                        .join("");
                    return `<div style="margin-bottom:12px"> ${displayLine}</div>`;
                }).join("");
                return `<div class="visit-${color}-popup-note">${text}</div>`;
            }
        },

        getBlueVisitPopupContent(content: any) {
            const element = this.blueNote.find((note: any) => note.id === content.Id);
            return `<div class="visit-blue-popup-note">${element.message}</div>`;
        },

        async openTaskManager(patient: BulkReassign) {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralVisitActions,
                action: "Central Patient Task manager"
            });
            // This has to be done because backend is sending -3 for hospice solution.
            let application;
            if (patient.Application === ApplicationEnum.AxxessHospice) {
                application = ApplicationEnum.AxxessHospiceFE;
            } else {
                application = patient.Application;
            }
            this.$router.push({
                path: "/axxess-central/task-manager-patient?",
                query: {
                    agencyId: patient.AgencyId,
                    fullname: `${patient.FirstName} ${patient.LastName}`,
                    application: application,
                    patientId: patient.Id,
                    branchId: patient.LocationId
                }
            });
        },

        async scheduleNewVisits(employee: BulkReassign) {
            const patientDetails = this.patientsList.find((patient: PatientCalendarModel) => employee.Id == patient.Id);
            const patientInfo = (({ AgencyName, FirstName, LastName, Mrn }) => ({
                AgencyName,
                FirstName,
                LastName,
                Mrn
            }))(patientDetails);
            const visitDetails: BulkReassign = Object.assign({}, patientInfo, employee);

            this.dialogRef = this.$dialog.open(ScheduleVisitDialog, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    visitDetails
                },
                onClose: async (options) => {
                    if (options.data === "success") {
                        this._analyticsService.logEvent({
                            category: AnalyticsCategory.CentralVisitActions,
                            action: "Central Patient calendar Add task"
                        });
                        this.$emit("calendarRefresh");
                    }
                    if (options.data === "reopen") {
                        this.scheduleNewVisits(employee);
                    }
                }
            });
        },

        openEvvHouse(arg: any) {
            const taskDetails = arg?.event?.extendedProps as IVisitDetails;
            const visitDetails: IVisitDetails = Object.assign({}, taskDetails);
            visitDetails.isEmployeeCalendar = true;
            this.dialogRef = this.$dialog.open(EvvDetails, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    visitDetails: visitDetails
                }
            });
        },

        async openProfile(employee: BulkReassign) {
            const patientDetails = this.patientsList.find((patient: PatientCalendarModel) => employee.Id == patient.Id);
            const patientInfo = (({ AgencyName, FirstName, LastName, Mrn }) => ({
                AgencyName,
                FirstName,
                LastName,
                Mrn
            }))(patientDetails);
            const patientData: BulkReassign = Object.assign({}, patientInfo, employee);

            this.dialogRef = this.$dialog.open(PatientProfile, {
                props: {
                    modal: true,
                    showHeader: false,
                    style: "width: 750px"
                },
                data: {
                    patientData
                }
            });
        },

        async handlePatientNameClick(resource: PatientCalendarModel) {
            let redirectResponse;
            let action = "";
            if (resource.Application === ApplicationEnum.AxxessHospice) {
                redirectResponse = {
                    redirectUrl: `${getHospiceRedirectionUrl(resource.AgencyId)}/patients/patient-chart/${resource.Id}?accountId=${resource.AgencyId}`,
                    isSuccessful: true
                };
                action = "AxxessHospice";
            } else {
                const isClinician = this._permissionManager.checkPermission(
                    ParentPermissionEnum.clinician,
                    PermissionActionEnum.canView
                );
                this.showLoading(true);
                redirectResponse = await this._navigationService.navigateToPatient({
                    agencyId: resource.AgencyId,
                    applicationId: resource.Application,
                    isClinician: isClinician,
                    patientId: resource.Id
                });
                action = resource.Application === ApplicationEnum.AgencyCore ? "AxxessHomeHealth" : "AxxessHomeCare";
                this.showLoading(false);
            }

            this._navigationService.redirect(redirectResponse, {
                category: AnalyticsCategory.CentralCalendarNavigation,
                action
            });
        },

        // NOTE: Uncomment when task navigation is needed
        // private async handleEventClick(eventObjectInput: any | ITaskByPatientResponse) {
        //     let eventData = eventObjectInput?.event?.extendedProps;
        // await this.navigateToTask(eventData);
        // }

        // private async navigateToTask(eventData: ITaskByPatientResponse) {
        //     let redirectResponse;
        //     let analyticsCategory = "Calendar-Task-Navigation";
        //     let action = "";

        //     if (eventData.Application === 256) {
        //         redirectResponse = {
        //             redirectUrl: `${config.hospiceUrl}/note/${eventData.Id}?accountId=${eventData.AgencyId}`,
        //             isSuccessful: true
        //         };
        //         action = "AxxessHospice";
        //     } else {
        //         let accessRoles = await this._accessService.getAccessRoles();
        //         redirectResponse = await this._navigationService.navigateToTask({
        //             agencyId: eventData.AgencyId,
        //             applicationId: eventData.Application,
        //             userId: eventData.UserId,
        //             isClinician: accessRoles.isClinician,
        //             patientId: eventData.PatientId,
        //             taskId: eventData.Id,
        //             episodeId: eventData.EpisodeId
        //         });
        //         action = eventData.Application === ApplicationEnum.AgencyCore ? "AxxessHomeHealth" : "AxxessHomeCare";
        //     }

        //     this._navigationService.redirect(redirectResponse, {
        //         category: analyticsCategory,
        //         action
        //     });
        // }

        attachTooltip(arg: any) {
            let element = $(arg?.el);
            let content = this.getTooltipContent(arg?.event?.extendedProps);
            (element as any).popover({
                html: true,
                content: content,
                trigger: "hover",
                placement: "top",
                container: "body"
                // delay: { show: 100, hide: 2000 },
                // sanitize: false //https://stackoverflow.com/questions/60001921/popover-html-content-with-button-and-input-not-rendering
            });
        },

        getTooltipContent(data: any) {
            let content = "";
            let evvContent = "";
            let scheduledTime = "";
            if (this.getEvvContent(data).length > 0 && this.getEvvContent(data) != "Not Yet Started") {
                evvContent = `<span>
                                <strong>EVV Time:</strong>
                                ${this.getEvvContent(data)}
                            </span>`;
            }
            if (!!data) {
                if (!!data.StartTime) {
                    scheduledTime = formatScheduledTime(data.StartTime);
                }
                content = `<div class="d-flex flex-column align-items-start event-tooltip font-size-xs">
                                ${data.BranchName ? `<span><strong>Branch:</strong> ${data.BranchName}</span>` : ``}
                                ${data.Payer ? `<span><strong>Payer:</strong> ${data.Payer}</span>` : ``}
                                <span><strong>Billable:</strong> ${!!data.IsBillable ? "Yes" : "No"}</span>
                                <span><strong>Payable:</strong> ${!!data.IsPayable ? "Yes" : "No"}</span>
                                ${
                                    data.ScheduledByFirstName
                                        ? `<span>
                                        <strong>Scheduled By:</strong>
                                            ${this.getUserName(data.ScheduledByFirstName, data.ScheduledByLastName)}
                                        </span>`
                                        : ``
                                }
                                ${evvContent}
                            <div>`;
            }
            return content;
        },

        getEvvContent(data: any) {
            let evvTime = "";
            if (data.Application === ApplicationEnum.AxxessHospiceFE) {
                return evvTime;
            } else if (!data.EvvTimeIn && !data.EvvTimeOut) {
                evvTime = "Not Yet Started";
            } else if (data.Status === "Completed") {
                const evvTimeIn = data.EvvTimeIn ? data.EvvTimeIn : "Not Collected";
                const evvTimeOut = data.EvvTimeIn ? data.EvvTimeOut : "Not Collected";
                evvTime = this.formatEVVTime(evvTimeIn, evvTimeOut);
            } else {
                const evvTimeIn = data.EvvTimeIn || "Not Collected";
                const evvTimeOut = data.EvvTimeOut || "Not Collected";
                evvTime = this.formatEVVTime(evvTimeIn, evvTimeOut);
            }
            return evvTime;
        },

        getUserName(firstName: string, lastName: string) {
            let userName = "";
            if (!firstName && !lastName) {
                userName = "Unassigned";
            } else {
                userName = formatName(firstName, lastName, "FN LN");
            }
            return userName;
        },

        attachPopover(arg: any) {
            let element = $(arg?.el).find(".more-btn");
            let extendedProps = arg?.event?.extendedProps;
            let startDate = new Date(Date.parse(extendedProps?.StartDate));
            let key = `${extendedProps.PatientId}${startDate.toLocaleDateString()}`;
            let tasksList = this.tasksMap?.get(key).slice(this.eventLimit, this.tasksList?.length);
            let content = "";

            tasksList.map((task: any) => {
                const isDraggable =
                    [EvvStatusHomeCareEnum.NotStarted].includes(task.EvvIconStatus as EvvStatusHomeCareEnum) ||
                    [VisitStatusEnum.NotStarted, VisitStatusEnum.NotYetDue].includes(task.Status as VisitStatusEnum);

                let taskContent = `<div class="fc-event ${isDraggable ? "fc-draggable" : ""} ax-calendar-event ${
                    ScheduleLegendState[task.State]
                } mb-1 more-event" id="${task.Id}@@${task.PatientId}@@${task.TaskName}" name=${task.TaskName} >
                    <span class="w-100 fw-bold text-truncate">
                        ${task.TaskName}
                    </span>
                    <span  class="d-flex align-items-center font-size-2xs">
                        <i class="fas fa-user me-1"></i>
                        <span>
                            ${this.getUserName(task.UserFirstName, task.UserLastName)}
                        </span>
                    </span>
                    <span class="w-100 font-size-3xs text-truncate">
                        ${task.Status}
                    </span>
                </div>`;
                content = content + taskContent;
                // this.attachMoreEventsTooltip(task);
            });
            content = `<div id='external-events'>${content} </div>`;
            (element as any)
                .popover({
                    html: true,
                    content: content,
                    trigger: "click focus",
                    placement: "top",
                    container: "body"
                })
                // .on("shown.bs.popover", (eve: any) => {
                //     const popover = document.getElementById("external-events") || null;
                //     if (!popover || !this.tasksList.length) {
                //         return;
                //     }
                //     const Dragger = new Draggable(popover, {
                //         itemSelector: ".fc-draggable",
                //         eventData: (eventEl) => {
                //             const eventid = eventEl.id.split("@@")[0];
                //             const currentTask = this.tasksList.find((task: ITaskByPatientResponse) => task.Id == eventid);
                //             return currentTask;
                //         }
                //     });
                // })
                .on("mouseenter", () => {
                    let parentDiv = element.closest("td > div");
                    parentDiv.css("z-index", "auto");
                })
                .on("mouseleave", () => {
                    let parentDiv = element.closest("td > div");
                    parentDiv.css("z-index", "");
                });
            // $(".fc-timeline-event")
            //     .off("click")
            //     // tslint:disable-next-line: only-arrow-functions
            //     .on("click", ".more-event", async function (event: any) {
            //         event.stopPropagation();
            //         let taskId = $(this).prop("id");
            //         if (!!taskId) {
            //             let task = self.tasksList.find((item) => item.Id === taskId);

            //             if (!!task) {
            //                 await self.navigateToTask(task);
            //             }
            //         }
            //     });
        },

        attachMoreEventsTooltip(task: ITaskByPatientResponse) {
            let content = this.getTooltipContent(task);
            $("body").on("mouseenter", `#${task.Id}`, () => {
                $(`#${task.Id}`)
                    .popover({
                        html: true,
                        content: content,
                        trigger: "hover",
                        placement: "top",
                        container: "body"
                    })
                    .popover("show");
            });
        },

        formatEVVTime(startTime: Date | string, endTime: Date | string) {
            if (startTime || endTime) {
                if (startTime === "Not Collected" || endTime === "Not Collected") {
                    return `${
                        startTime === "Not Collected" ? "Not Collected" : moment(startTime).format("hh:mm A")
                    } - ${endTime === "Not Collected" ? "Not Collected" : moment(endTime).format("hh:mm A")}`;
                }
                if (moment(startTime).isBefore(endTime, "day")) {
                    return `${moment(startTime).format("hh:mm A")} - ${moment(endTime).format("MM/DD/YYYY hh:mm A")}`;
                } else {
                    return `${moment(startTime).format("hh:mm A")} - ${moment(endTime).format("hh:mm A")}`;
                }
            } else return "";
        },

        getComment(content: IGetTaskResponseByUser): string {
            if (content.Comments.length === 0) return "";
            const response = content.Comments.find(
                (comment: IGetTaskComment) =>
                    comment.CommentColor.toLowerCase() === CommentColorEnum.yellow.toLowerCase()
            );
            return response?.Comment;
        },

        filterDuplicateEvent(event: any) {
            let calendarApi = event.view.calendar;
            let calendarloadedEvents = calendarApi.getEvents();
            if (calendarloadedEvents.length != this.visitActions.length) {
                let existingEvents = calendarloadedEvents;
                const uniqueEvents = Array.from(new Map(existingEvents.map((e) => [e.id, e])).values());
                calendarApi.removeAllEvents();
                uniqueEvents.forEach((e) => calendarApi.addEvent(e));
            }
        }
    }
});
